// Cliente HTTP de los "Avisos operativos" de la IA del helpdesk
// (helpdesk/ai-notices): avisos con vigencia (p. ej. un feriado sin retiros)
// que la IA considera mientras están vigentes.
//
// Calca el patrón de aiMemoryApi.js: mismo BFF, mismos headers, mismos errores
// tipados con `.status` para poder distinguir "ruta aún no desplegada"
// (isEndpointMissing, re-exportado desde aiSettingsApi — no se duplica) de un
// error real.
//
// El id de un aviso viaja en la URL: se codifica con encodeURIComponent para
// que un id con "/" o "?" no termine apuntando a otra ruta. Prohibido loguear
// payloads acá.

import { isEndpointMissing } from "./aiSettingsApi";

// Typed error shared by every helpdesk client (status, code y el mensaje de
// negocio que apiErrorMessage sabe leer de vuelta).
import { throwFetchApiError } from "../../../../utils/apiErrorMessage";
import { authFetch } from "../../../../services/authFetch";

// Re-export: las vistas importan todo desde este módulo, pero la implementación
// vive en un solo lugar (aiSettingsApi).
export { isEndpointMissing };

// REACT_APP_API_CONNECTION ya trae /api/v1/ al final; se recorta para armar
// rutas /api/v1/... sin duplicar el prefijo.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(/\/api\/v1\/?$/, "");

const NOTICES_PATH = `${BFF_BASE_URL}/api/v1/helpdesk/ai-notices`;

function buildAuthHeaders(extra = {}) {
  // El Authorization lo pone authFetch (access vigente + un reintento ante 401).
  return {
    "x-app": "web",
    application: "web",
    ...extra,
  };
}

function noticePath(id) {
  return `${NOTICES_PATH}/${encodeURIComponent(id)}`;
}

/**
 * Avisos del courier (vigentes, próximos y vencidos de los últimos días).
 * Contrato: { ok, notices: Notice[] }. La clasificación por vigencia NO viene
 * del backend: se calcula en aiNotices.js comparando instantes.
 * @returns {Promise<object>}
 */
export async function getAiNotices() {
  const response = await authFetch(NOTICES_PATH, { headers: buildAuthHeaders() });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Crea un aviso. Contrato: { ok, notice }.
 * `body` viaja completo: { text, starts_at, ends_at, audiences, no_operation }.
 * @param {{text: string, starts_at: string|null, ends_at: string|null, audiences: string[], no_operation: boolean}} body
 * @returns {Promise<object>}
 */
export async function createAiNotice(body) {
  const response = await authFetch(NOTICES_PATH, {
    method: "POST",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Edita un aviso. Contrato: { ok, notice }.
 * El PATCH lleva el set COMPLETO de campos editables (no un diff): el backend
 * reemplaza la vigencia entera y así no queda mezcla de versiones.
 * @param {string} id
 * @param {{text: string, starts_at: string|null, ends_at: string|null, audiences: string[], no_operation: boolean}} body
 * @returns {Promise<object>}
 */
export async function updateAiNotice(id, body) {
  const response = await authFetch(noticePath(id), {
    method: "PATCH",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Borra un aviso. Un 204 sin body también resuelve como { ok: true }: parsear
 * JSON ahí tira SyntaxError.
 * @param {string} id
 * @returns {Promise<{ok: boolean}>}
 */
export async function deleteAiNotice(id) {
  const response = await authFetch(noticePath(id), {
    method: "DELETE",
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  if (response.status === 204) return { ok: true };
  return response.json().catch(() => ({ ok: true }));
}
